import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Activity, Target, Flame, Clock, TrendingUp, AlertCircle, Calendar, Download, Mail, Loader2, Trophy, Heart } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, BarChart, Bar } from "recharts";
import { API_BASE } from "@/lib/api";

const RISK_BADGE = {
  "düşük": "bg-green-100 text-green-800",
  "orta": "bg-amber-100 text-amber-800",
  "yüksek": "bg-orange-100 text-orange-800",
  "çok yüksek": "bg-red-100 text-red-800",
};

export default function ParentDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [targetRisk, setTargetRisk] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);

  const load = () => {
    api.get("/parent/child-summary")
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.detail || "Veriler alınamadı"));
  };
  useEffect(() => {
    load();
    api.get("/parent/target-risk")
      .then((r) => setTargetRisk(r.data))
      .catch(() => setTargetRisk(null));
  }, []);

  const refreshRisk = async () => {
    setRiskLoading(true);
    try {
      const r = await api.get("/parent/target-risk");
      setTargetRisk(r.data);
    } finally {
      setRiskLoading(false);
    }
  };

  const link = async () => {
    if (!linkEmail) return;
    setLinking(true);
    try {
      await api.post("/parent/link-student", { student_email: linkEmail.trim().toLowerCase() });
      toast.success("Öğrenci bağlandı");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bağlanamadı");
    } finally {
      setLinking(false);
    }
  };

  if (error) {
    return (
      <Card className="p-6 rounded-2xl border-slate-200 max-w-md mx-auto" data-testid="parent-link-card">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <h3 className="font-heading font-semibold text-slate-900">Öğrenci Bağla</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">{error}</p>
        <div className="flex gap-2">
          <Input value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)}
            placeholder="ela@akademi.com"
            data-testid="parent-link-input"
            className="rounded-xl" />
          <Button onClick={link} disabled={linking}
            data-testid="parent-link-btn"
            className="bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl">Bağla</Button>
        </div>
      </Card>
    );
  }

  if (!data) return <div className="text-slate-300">Yükleniyor…</div>;

  const s = data.summary;

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem("ela_token");
      const resp = await fetch(`${API_BASE}/parent/reports/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("pdf");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ela_rapor_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF indirilemedi");
    }
  };

  const queueEmail = async () => {
    try {
      await api.post("/reports/email-weekly");
      toast.success("Haftalık rapor email kuyruğuna eklendi.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <div className="space-y-5 ela-fade-in" data-testid="parent-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center text-2xl">
            {data.student.avatar_emoji || "🎓"}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">{data.student.full_name}</h1>
            <p className="text-sm text-slate-500">Çocuğunuzun gelişim raporu</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadPDF} variant="outline"
            data-testid="parent-pdf-btn" className="rounded-xl">
            <Download className="w-4 h-4 mr-2" /> PDF Rapor
          </Button>
          <Button onClick={queueEmail}
            data-testid="parent-email-btn"
            className="rounded-xl bg-[#FF6B6B] hover:bg-[#FF5252]">
            <Mail className="w-4 h-4 mr-2" /> Haftalık Email
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="LGS'ye" value={`${s.days_until_lgs} gün`} icon={Calendar} />
        <Metric label="Çalışma Serisi" value={`${s.streak_days} gün`} icon={Flame} />
        <Metric label="Bu Hafta Süre" value={`${s.week_minutes} dk`} icon={Clock} />
        <Metric label="Bu Hafta Soru" value={s.week_questions} icon={Target} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold mb-1">Son 7 Gün</h3>
          <p className="text-xs text-slate-500 mb-3">Günlük çözdüğü soru sayısı ve çalışma süresi</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.daily_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="questions" fill="#FF6B6B" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold mb-1">Genel Doğruluk</h3>
          <div className="font-display text-5xl font-bold text-slate-900 mt-3">%{s.accuracy}</div>
          <p className="text-xs text-slate-500 mt-2">{s.total_correct}/{s.total_questions} doğru</p>
          <p className="text-xs text-slate-500 mt-1">{s.completed_topics} konu tamamlandı</p>
        </Card>
      </div>

      <Card className="p-5 rounded-2xl border-slate-200">
        <h3 className="font-heading font-semibold mb-3">Ders Bazında</h3>
        {s.subject_breakdown.length === 0 ? (
          <p className="text-sm text-slate-500">Henüz quiz çözmedi.</p>
        ) : (
          <div className="space-y-2.5">
            {s.subject_breakdown.map((sub) => (
              <div key={sub.subject_key} className="flex items-center gap-3">
                <span className="text-sm w-32 shrink-0">{sub.subject_name}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF6B6B]" style={{ width: `${sub.accuracy}%` }} />
                </div>
                <span className="text-xs w-20 text-right">%{sub.accuracy} ({sub.total})</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {s.weak_topics?.length > 0 && (
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" /> Eksik Konular
          </h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {s.weak_topics.map((w) => (
              <div key={w.topic_code} className="p-3 rounded-xl border border-slate-200 bg-amber-50/30">
                <div className="text-sm font-medium text-slate-900">{w.topic_name}</div>
                <div className="text-xs text-slate-500">{w.subject_name} · %{w.accuracy}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Hedef Lise Bölümü */}
      {targetRisk?.target && targetRisk.assessment && (
        <Card className="p-5 rounded-2xl border-slate-200" data-testid="parent-target-risk">
          <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#FF6B6B]" />
              <h3 className="font-heading font-semibold">Hedef: {targetRisk.target.school_name}</h3>
            </div>
            <Badge className={`${RISK_BADGE[targetRisk.gap?.risk_level] || ""} border-0`}>
              Risk: {targetRisk.gap?.risk_level}
            </Badge>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4 p-4 rounded-xl bg-slate-50">
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Şu an tahmini</div>
              <div className="font-display text-xl font-bold text-slate-900">{targetRisk.estimate.estimated_score}</div>
            </div>
            <div>
              <div className="text-[10px] text-rose-600 uppercase">Hedef güvenli</div>
              <div className="font-display text-xl font-bold text-[#FF6B6B]">{targetRisk.target.safe_score}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Fark</div>
              <div className="font-display text-xl font-bold text-slate-900">{targetRisk.gap?.gap_points} pt</div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">Gidişat</div>
              <p className="text-sm text-slate-700">{targetRisk.assessment.gidisat_ozeti}</p>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">Bu Haftaki Katkı</div>
              <p className="text-sm text-slate-700">{targetRisk.assessment.bu_hafta_katki}</p>
            </div>

            {targetRisk.assessment.kritik_3_eksik?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Kritik 3 Eksik</div>
                <ul className="space-y-1">
                  {targetRisk.assessment.kritik_3_eksik.map((k, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" /> {k}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {targetRisk.assessment.aile_destegi?.length > 0 && (
              <div className="p-4 rounded-xl bg-rose-50/40 border border-rose-100">
                <div className="text-xs font-semibold text-rose-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Heart className="w-3 h-3" /> Aile Olarak Bu Hafta
                </div>
                <ul className="space-y-1.5">
                  {targetRisk.assessment.aile_destegi.map((a, i) => (
                    <li key={i} className="text-sm text-slate-800 flex gap-2">
                      <span className="text-[#FF6B6B] font-bold shrink-0">{i + 1}.</span> {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {targetRisk.assessment.uyari && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex gap-2 items-start text-sm text-amber-900">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{targetRisk.assessment.uyari}</span>
                </div>
              </div>
            )}

            {targetRisk.assessment.umut_mesaji && (
              <p className="text-sm text-slate-600 italic border-l-2 border-rose-200 pl-3">
                {targetRisk.assessment.umut_mesaji}
              </p>
            )}
          </div>

          <Button onClick={refreshRisk} variant="outline" size="sm"
            disabled={riskLoading}
            data-testid="refresh-risk-btn"
            className="mt-4 rounded-xl">
            {riskLoading ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : null}
            Değerlendirmeyi Yenile
          </Button>
        </Card>
      )}

      {targetRisk && !targetRisk.target && (
        <Card className="p-5 rounded-2xl border-slate-200 border-dashed text-center">
          <Target className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">
            Henüz hedef okul belirlenmedi. Ela "Hedef" sekmesinden seçim yaparsa burada gelişim takibi görünür.
          </p>
        </Card>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center mb-2">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-display text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
